import get from 'lodash/get';

import { readMinSharedMemoryGiFromDeployConfig } from '../../../capabilities/image/dockerProbe';
import { buildImageQuery } from '../../../capabilities/image/utils';
import type { StartupArgDefinition } from '../../../capabilities/startup-command-args/utils';
import { clearSystemDiskFields, resolveSystemDiskSizePaths } from '../../../capabilities/system-disk-size';
import type { LinkageRule } from '../../../platform/linkage/types';
import { isOrderResourceEditMode } from '../../../platform/resource-form/formContextRegistry';
import { parseContainerDeployConfigObject } from '../../../utils/parseContainerDeployConfigObject';
import {
  hasContainerDeployConfigContent,
  readContainerImageItemFromFormValues,
} from '../../_shared/common/container-deploy-config/containerDeployConfig';
import { readContainerImageArchitecture, readGpu } from '../../_shared/common/formHelpers';
import {
  createNetworkContextLinkageRules,
  createStorageContextLinkageRules,
} from '../../_shared/image-network-linkage/linkage/contextRules';
import { createImageKeySwitchTracker } from '../../_shared/image-network-linkage/linkage/imageSwitchTracker';
import { CI_BASIC_IMAGE_KEY_FIELD, CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD } from './constants';
import {
  CI_DEFAULT_DEPLOY_CONFIG_BINDING,
  applyContainerDeployConfigToForm,
  clearContainerDeployConfigFromForm,
  readStartupArgDefinitionsFromImage,
} from './containerDeployConfig';

/** CI 系统盘表单路径（与 system-disk-size binding 一致） */
const CI_SYSTEM_DISK_PATHS = resolveSystemDiskSizePaths('', {
  fieldSegment: 'systemDiskSize',
  pricingSegment: 'systemDiskPricing',
});

export interface CiImageLinkageProps {
  imageQuery?: Record<string, unknown>;
  namespace?: string;
}

export interface CiComputeLinkageProps {
  architectureOptions?: string[];
  namespace?: string;
  vmName?: string;
  cpuMin?: number;
  memoryMin?: number;
  sharedMemoryMin?: number;
}

export interface CiNetworkLinkageProps {
  workspace?: string;
  datacenter?: string;
}

export interface CiStorageLinkageProps {
  namespace?: string;
  containerName?: string;
  datacenter?: string;
}

export interface CiStartupCommandArgsLinkageProps {
  argDefinitions?: StartupArgDefinition[];
}

export type CiLinkagePropsMap = Record<string, unknown> & {
  image?: CiImageLinkageProps;
  'system-disk-size'?: Record<string, unknown>;
  'startup-command-args'?: CiStartupCommandArgsLinkageProps;
  compute?: CiComputeLinkageProps;
  network?: CiNetworkLinkageProps;
  'container-storage'?: CiStorageLinkageProps;
};

const ciImageKeySwitchTracker = createImageKeySwitchTracker();
/** 系统盘回填共用镜像切换 tracker，避免与 deployConfig 效果二次触发 */
const ciSystemDiskImageKeySwitchTracker = createImageKeySwitchTracker();

function applyDeployConfigFromImageEffect(ctx: { form: import('@arco-design/web-react').FormInstance; values: Record<string, unknown> }) {
  const imageKey = String(get(ctx.values, CI_BASIC_IMAGE_KEY_FIELD) ?? get(ctx.values, 'basic.image.key') ?? '').trim();
  const imageSwitched = ciImageKeySwitchTracker.consumeImageSwitch(ctx.form, imageKey || undefined);

  // 仅镜像 key 实际变更时清场/重灌，避免其它字段（如架构）触发 linkage 误伤预置数据
  if (!imageSwitched) return;

  /** 下单：以 SKU preset 为底合并（含清空镜像 → 回落仅 SKU）；直建仍整表清场再灌 */
  const orderMerge = isOrderResourceEditMode(ctx.form);

  if (!imageKey) {
    if (orderMerge) {
      applyContainerDeployConfigToForm(ctx.form, null, CI_DEFAULT_DEPLOY_CONFIG_BINDING, {
        clearResourceCompute: false,
        skipClear: true,
        mergeWithSkuPreset: true,
      });
    } else {
      clearContainerDeployConfigFromForm(ctx.form, CI_DEFAULT_DEPLOY_CONFIG_BINDING);
    }
    return;
  }

  if (!orderMerge) {
    clearContainerDeployConfigFromForm(ctx.form, CI_DEFAULT_DEPLOY_CONFIG_BINDING);
  }

  const imageItem = readContainerImageItemFromFormValues(ctx.values, CI_DEFAULT_DEPLOY_CONFIG_BINDING.imagePrefix);
  if (orderMerge) {
    applyContainerDeployConfigToForm(ctx.form, imageItem, CI_DEFAULT_DEPLOY_CONFIG_BINDING, {
      clearResourceCompute: false,
      skipClear: true,
      mergeWithSkuPreset: true,
    });
    return;
  }

  if (imageItem && hasContainerDeployConfigContent(imageItem.containerDeployConfig)) {
    applyContainerDeployConfigToForm(ctx.form, imageItem, CI_DEFAULT_DEPLOY_CONFIG_BINDING, {
      clearResourceCompute: false,
      skipClear: true,
    });
  }
}

/**
 * 选镜像后系统盘不自动回填容量（避免默认 10Gi 直接出卡片）。
 * 清空镜像时清掉系统盘，由用户通过「系统盘配置」添加入口自行配置。
 */
function applySystemDiskSizeFromImageEffect(ctx: { form: import('@arco-design/web-react').FormInstance; values: Record<string, unknown> }) {
  const imageKey = String(get(ctx.values, CI_BASIC_IMAGE_KEY_FIELD) ?? get(ctx.values, 'basic.image.key') ?? '').trim();
  const imageSwitched = ciSystemDiskImageKeySwitchTracker.consumeImageSwitch(ctx.form, imageKey || undefined);
  if (!imageSwitched) return;

  if (!imageKey) {
    clearSystemDiskFields(ctx.form, CI_SYSTEM_DISK_PATHS);
  }
}

/** 容器实例直建 linkage：对齐云主机镜像架构 ↔ 资源配置架构 */
export const CI_LINKAGE_RULES: LinkageRule<CiLinkagePropsMap>[] = [
  {
    id: 'basicImage.applyDeployConfig',
    watch: CI_BASIC_IMAGE_KEY_FIELD,
    target: 'image',
    effect: applyDeployConfigFromImageEffect,
  },
  {
    id: 'basicImage.systemDiskSize',
    watch: CI_BASIC_IMAGE_KEY_FIELD,
    target: 'system-disk-size',
    effect: applySystemDiskSizeFromImageEffect,
  },
  {
    id: 'basicImage.startupArgDefinitions',
    watch: CI_BASIC_IMAGE_KEY_FIELD,
    target: 'startup-command-args',
    derive: ({ values }) => {
      const imageKey = String(get(values, CI_BASIC_IMAGE_KEY_FIELD) ?? '').trim();
      if (!imageKey) {
        return { argDefinitions: [] as const };
      }
      const imageItem = readContainerImageItemFromFormValues(values, CI_DEFAULT_DEPLOY_CONFIG_BINDING.imagePrefix);
      if (!imageItem) {
        return { argDefinitions: [] as const };
      }
      return {
        argDefinitions: readStartupArgDefinitionsFromImage(imageItem),
      };
    },
  },
  {
    id: 'arch.imageQuery',
    watch: [CI_BASIC_IMAGE_KEY_FIELD, 'resource.gpu'],
    target: 'image',
    derive: ({ values }) => ({
      imageQuery: buildImageQuery(readContainerImageArchitecture(values), readGpu(values), 'Docker'),
    }),
  },
  {
    id: 'baseInfo.computeContext',
    watch: ['namespace', 'name'],
    target: 'compute',
    derive: ({ values }) => ({
      namespace: values.namespace as string | undefined,
      vmName: values.name as string | undefined,
    }),
  },
  {
    id: 'image.computeMins',
    watch: [CI_BASIC_IMAGE_KEY_FIELD, CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD],
    target: 'compute',
    derive: ({ form, values }) => {
      const imageKey = String(
        form.getFieldValue(CI_BASIC_IMAGE_KEY_FIELD) ?? get(values, CI_BASIC_IMAGE_KEY_FIELD) ?? values[CI_BASIC_IMAGE_KEY_FIELD] ?? '',
      ).trim();
      if (!imageKey) return { cpuMin: undefined, memoryMin: undefined, sharedMemoryMin: undefined };
      const scheduling = (form.getFieldValue(CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD) ??
        values[CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD] ??
        get(values, CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD)) as { minCpuCores?: number; minMemoryGi?: number } | undefined;
      const imageItem = readContainerImageItemFromFormValues(values, CI_DEFAULT_DEPLOY_CONFIG_BINDING.imagePrefix);
      const deployConfig = parseContainerDeployConfigObject(imageItem?.containerDeployConfig);
      return {
        cpuMin: scheduling?.minCpuCores,
        memoryMin: scheduling?.minMemoryGi,
        sharedMemoryMin: readMinSharedMemoryGiFromDeployConfig(deployConfig),
      };
    },
  },
  {
    id: 'baseInfo.imageContext',
    watch: ['namespace', 'name'],
    target: 'image',
    derive: ({ values }) => ({
      namespace: values.namespace as string | undefined,
    }),
  },
  ...createNetworkContextLinkageRules<CiLinkagePropsMap>(),
  ...createStorageContextLinkageRules<CiLinkagePropsMap>(['container-storage']),
];
